<template>
    <div class="signup">
        <HeaderCentered
            :logo="$whitelabel.resources.logo"
        >
            <template #extra>
                <div class="ot-text-intro">
                    {{ $t('login.signup.title') }}
                </div>
            </template>
            <template #subtitle>
                <h5 v-if="error" class="warning remove-margin-bottom">
                    {{ $t(error) }}
                </h5>
            </template>
        </HeaderCentered>
        <OtCardContent class="signup__content">
            <OtInputField :label="$t('login.signup.question.text')">
                <OtInputSelectBar
                    class="is-fullwidth"
                    v-model="chosenOption"
                    :options="options"
                />
            </OtInputField>
            <div
                v-if="chosenOption === 'ticket_buyer'"
                class="signup__content__ticket_buyer"
            >
                <div v-if="url">
                    <OtAside
                        icon="ticket"
                        type="is-info"
                    >
                        <template #description>
                            <i18n path="login.signup.ticket_buyer.notification.text">
                                <template #ticket_support>
                                    <a
                                        class="signup__content__ticket_buyer__terms ot-clickable"
                                        :href="url"
                                        target="_blank"
                                    >
                                        {{ $t('login.signup.ticket_buyer.notification.ticket_support') }}
                                    </a>
                                </template>
                            </i18n>
                        </template>
                    </OtAside>
                </div>
                <div v-else>
                    <OtAside
                        icon="oti-ticket"
                        type="is-danger"
                    >
                        <template #description>
                            <i18n path="login.signup.ticket_buyer.notification.ticket_support_email">
                                <template #email>
                                    <a
                                        class="signup__content__ticket_buyer__terms ot-clickable"
                                        :href="'mailto:' + email"
                                    >
                                        {{ email }}
                                    </a>
                                </template>
                            </i18n>
                        </template>
                    </OtAside>
                    <a
                        class="signup__content__ticket_buyer__email_support ot-button"
                        :href="'mailto:' + email"
                        :aria-label="ticketSupportEmailTitle"
                    >
                            {{ $t('login.signup.ticket_buyer.ticket_support_email.text') }}
                    </a>
                </div>
            </div>
            <div v-else class="signup__content__organiser">
                <OtInputField
                    :label="$t('login.signup.organiser.form.name.label')"
                    :error="getValidationError(validationErrors.company_name, validationRules.company_name)"
                    :required="rules.company_name && rules.company_name.includes('required')"
                    :optional="rules.company_name && rules.company_name.includes('optional')"
                >
                    <input
                        class="is-fullwidth"
                        :invalid="!!getValidationError(validationErrors.company_name, validationRules.company_name)"
                        :placeholder="$t('login.signup.organiser.form.name.placeholder').toString()"
                        v-model="form.company_name"
                        @input="validationErrors.company_name = []"
                        autofocus
                    />
                </OtInputField>
                <OtInputField
                    :label="$t('login.signup.organiser.form.country.label')"
                    :error="getValidationError(validationErrors.company_country, validationRules.company_country)"
                    :required="rules.company_country && rules.company_country.includes('required')"
                    :optional="rules.company_country && rules.company_country.includes('optional')"
                >
                    <OtInputSelect
                        class="is-fullwidth"
                        v-model="form.company_country"
                        :options="countries"
                        :searchable="true"
                        :internal-search="true"
                        :searchLabel="$t('login.signup.organiser.form.country.search')"
                        :invalid="!!getValidationError(validationErrors.company_country, validationRules.company_country)"
                        @input="onCompanyCountrySelection()"
                    />
                    <span class="ot-input-sublabel country-timezone" v-if="!timeZoneSelection">
                      <OtIcon type="location" size="small" />
                        <span class="country-timezone__timezone">{{ form.company_timezone }}</span>
                      <OtButton class="is-link is-tiny" @click="timeZoneSelection = true">
                        {{ $t('login.signup.organiser.form.country.change_timezone') }}
                      </OtButton>
                    </span>
                </OtInputField>
                <OtInputField
                    :label="$t('login.signup.organiser.form.timezone.label')"
                    :error="getValidationError(validationErrors.company_timezone, validationRules.company_timezone)"
                    v-if="timeZoneSelection || form.company_timezone === null"
                    :required="rules.company_timezone && rules.company_timezone.includes('required')"
                    :optional="rules.company_timezone && rules.company_timezone.includes('optional')"
                >
                    <OtInputSelect
                        class="is-fullwidth"
                        v-model="form.company_timezone"
                        :options="$localization.supportedTimezones"
                        :searchable="true"
                        :internal-search="true"
                        :invalid="!!getValidationError(validationErrors.company_timezone, validationRules.company_timezone)"
                        @input="onTimezoneSelection()"
                    />
                </OtInputField>
                <OtInputField
                    :label="$t('login.signup.organiser.form.user.name.label')"
                    :error="getValidationError(validationErrors.user_name, validationRules.user_name)"
                    :required="rules.user_name && rules.user_name.includes('required')"
                    :optional="rules.user_name && rules.user_name.includes('optional')"
                >
                    <OtInputText
                        class="is-fullwidth"
                        :placeholder="$t('login.signup.organiser.form.user.name.placeholder')"
                        :invalid="!!getValidationError(validationErrors.user_name, validationRules.user_name)"
                        v-model="form.user_name"
                        @input="validationErrors.user_name = []"
                    />
                </OtInputField>
                <OtInputField
                    :label="$t('login.signup.organiser.form.user.phone.label')"
                    :error="getValidationError(validationErrors.user_phone, validationRules.user_phone)"
                    :required="rules.user_phone && rules.user_phone.includes('required')"
                >
                    <OtInputPhone
                        class="is-fullwidth"
                        v-model="form.user_phone"
                        :options="countries"
                        :searchable="true"
                        @blur="isUserPhoneDirty = true"
                        :invalid="!!getValidationError(validationErrors.user_phone, validationRules.user_phone)"
                        @input="validationErrors.user_phone = []"
                    />
                </OtInputField>
                <OtInputField
                    :label="$t('login.signup.organiser.form.user.email.label')"
                    :error="getValidationError(validationErrors.user_email, validationRules.user_email)"
                    required
                >
                    <OtInputText
                        class="is-fullwidth"
                        :placeholder="$t('login.signup.organiser.form.user.email.placeholder')"
                        :invalid="!!getValidationError(validationErrors.user_email, validationRules.user_email)"
                        v-model="form.user_email"
                        @input="validationErrors.user_email = []"
                    />
                </OtInputField>
                <OtInputField
                    :label="$t('login.signup.organiser.form.user.password.label')"
                    :error="getValidationError(validationErrors.user_password, validationRules.user_password)"
                    :required="rules.user_password && rules.user_password.includes('required')"
                    :optional="rules.user_password && rules.user_password.includes('optional')"
                    >
                    <OtInputPassword
                        class="is-fullwidth"
                        :placeholder="$t('login.signup.organiser.form.user.password.placeholder')"
                        :invalid="!!getValidationError(validationErrors.user_password, validationRules.user_password)"
                        v-model="form.user_password"
                        @input="validationErrors.user_password = []"
                    />
                </OtInputField>
                <OtInputField
                    :label="$t('login.signup.organiser.form.user.confirm_password.label')"
                    :error="getValidationError(
                        validationErrors.user_password_confirmation,
                        validationRules.user_password_confirmation,
                       )"
                    :required="rules.user_password_confirmation && rules.user_password_confirmation.includes('required')"
                    :optional="rules.user_password_confirmation && rules.user_password_confirmation.includes('optional')"
                >
                    <OtInputPassword
                        class="is-fullwidth"
                        :placeholder="$t('login.signup.organiser.form.user.confirm_password.placeholder')"
                        v-model="form.user_password_confirmation"
                        :invalid="!!getValidationError(
                            validationErrors.user_password_confirmation,
                            validationRules.user_password_confirmation,
                            )"
                        @input="validationErrors.user_password_confirmation = []"
                    />
                </OtInputField>
                    <div class="ot-checkbox terms">
                        <input
                            class="terms-checkbox"
                            type="checkbox"
                            id="terms"
                            v-model="form.accept_tos"
                            @click="form.accept_tos = !form.accept_tos"
                        />
                        <!-- TODO replace terms link and get if it from whitelabel config-->
                        <label class="terms-label" for="terms">
                            <i18n path="login.signup.organiser.form.terms.first">
                                <template #tos>
                                    <a
                                        v-if="$whitelabel.dashboard.terms_url"
                                        class="terms-tos-first ot-link"
                                        :href="$whitelabel.dashboard.terms_url"
                                        target="_blank"
                                    >
                                        {{ $t('login.signup.organiser.form.terms.tos') }}
                                    </a>
                                    <span v-else>
                                        {{ $t('login.signup.organiser.form.terms.tos') }}
                                    </span>
                                </template>
                                <template #entity>
                                    <span class="terms-entity terms-entity-first">
                                        {{ $whitelabel.name }}
                                    </span>
                                </template>
                            </i18n>
                            <span
                                v-if="rules.accept_tos && rules.accept_tos.includes('required')"
                                class="ot-input-label--required"
                            >
                                *
                            </span>
                        </label>
                    </div>
                <OtButton
                    class="ot-button is-fullwidth signup-button"
                    @click="submit"
                    :disabled="!formCompleted"
                    :title="$t('login.signup.organiser.action.title')"
                >
                    {{ $t('login.signup.organiser.action.text') }}
                </OtButton>
            </div>
            <div class="signup__content__back">
                <OtButton
                    class="ot-button is-text is-small"
                    @click="$router.push({name: 'login', query: $route.query})"
                    :title="$t('login.signup.back.title')"
                >
                    <i class="oti oti-arrow-left ot-button-icon--left is-small"/>
                    {{ $t('login.signup.back.text') }}
                </OtButton>
            </div>
        </OtCardContent>
    </div>
</template>

<script lang="ts">
import type { GlobalizeCountryInfo } from '@openticket/vue-localization';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import urlJoin from 'url-join';
import Axios, { type AxiosError } from 'axios';
import HeaderCentered from '../HeaderCentered.vue';
import OtButton from '../OtButton.vue';
import {
    computeAutoselectDashboardQueryString,
} from '../../services/util/autoselectdashboard';
import { rudderAnalyticsIdentify, rudderAnalyticsTrack } from '../../services/util/rudderstack';

interface SignupValidationError {
    accept_tos?: string[] | null,
    company_country?: string[] | null,
    company_currency?: string[] | null,
    company_locale?: string[] | null,
    company_name?: string[] | null,
    company_timezone?: string[] | null,
    company_website?: string[] | null,
    user_name?: string[] | null,
    user_email?: string[] | null,
    user_password?: string[] | null,
    user_password_confirmation?: string[] | null,
    user_phone?: string[] | null,
}

interface SignupRules {
    accept_tos: string[],
    company_country: string[],
    company_currency: string[],
    company_name: string[],
    company_locale: string[],
    company_timezone: string[],
    company_website: string[],
    user_email: string[],
    user_name: string[],
    user_password: string[],
    user_password_confirmation: string[],
    user_phone: string[],
}

interface SignupFormData {
    client_id: string | null;
    company_name: string | null;
    company_website: string | null;
    company_country: string | null;
    company_locale: string | null;
    company_timezone: string | null;
    company_currency: string | null;
    accept_tos: boolean;
    user_default_dashboard: string;
    user_name: string | null;
    user_email: string | null;
    user_password: string | null;
    user_password_confirmation: string | null;
    user_phone: string | null;
}

@Component({ components: { HeaderCentered, OtButton } })
export default class SignupView extends Vue {

    options: {[key: string]: string} = {
        organiser: this.$t('login.signup.question.organiser') as string,
        ticket_buyer: this.$t('login.signup.question.ticket_buyer') as string,
    };

    chosenOption = 'organiser';

    terms = false;

    rules: SignupRules = {
        accept_tos: [],
        company_country: [],
        company_currency: [],
        company_locale: [],
        company_name: [],
        company_timezone: [],
        company_website: [],
        user_email: [],
        user_name: [],
        user_password: [],
        user_password_confirmation: [],
        user_phone: [],
    };

    validationRules: {
        accept_tos: {
            [key: string]: string;
        };
        company_country: {
            [key: string]: string;
        };
        company_currency: {
            [key: string]: string;
        };
        company_locale: {
            [key: string]: string;
        };
        company_name: {
            [key: string]: string;
        };
        company_timezone: {
            [key: string]: string;
        };
        company_website: {
            [key: string]: string;
        };
        user_email: {
            [key: string]: string;
        };
        user_name: {
            [key: string]: string;
        };
        user_password: {
            [key: string]: string;
        };
        user_password_confirmation: {
            [key: string]: string;
        };
        user_phone: {
            [key: string]: string;
        };
    } = {
            accept_tos: { attribute: this.$t('login.signup.organiser.form.accept_tos.label') },
            company_country: { attribute: this.$t('login.signup.organiser.form.country.label') },
            company_currency: { attribute: this.$t('login.signup.organiser.form.currency.label') },
            company_locale: { attribute: this.$t('login.signup.organiser.form.locale.label') },
            company_name: { attribute: this.$t('login.signup.organiser.form.name.label') },
            company_timezone: { attribute: this.$t('login.signup.organiser.form.timezone.label') },
            company_website: { attribute: this.$t('login.signup.organiser.form.website.label') },
            user_email: { attribute: this.$t('login.signup.organiser.form.user.email.label') },
            user_name: { attribute: this.$t('login.signup.organiser.form.user.name.label') },
            user_password: { attribute: this.$t('login.signup.organiser.form.user.password.label') },
            user_password_confirmation: { attribute: this.$t('login.signup.organiser.form.user.password_confirmation.label') },
            user_phone: { attribute: this.$t('login.signup.organiser.form.user.phone.label') },
        };

    validationErrors: SignupValidationError = {
        accept_tos: null,
        company_country: null,
        company_currency: null,
        company_locale: null,
        company_name: null,
        company_timezone: null,
        company_website: null,
        user_email: null,
        user_name: null,
        user_password: null,
        user_password_confirmation: null,
        user_phone: null,
    };

    // TODO fix password_confirmation for when backend route allows for it
    form: SignupFormData = {
        accept_tos: false,
        client_id: null,
        company_country: null,
        company_currency: null,
        company_locale: null,
        company_name: null,
        company_timezone: null,
        company_website: null,
        user_default_dashboard: this.$whitelabel.dashboard.preferred_dashboard || 'auto-legacy',
        user_email: null,
        user_name: null,
        user_password: null,
        user_password_confirmation: null,
        user_phone: null,
    };

    localizationListener!: number;
    isCompanyCountryDirty = false;
    isCompanyTimezoneDirty = false;
    isUserPhoneDirty = false;

    timeZoneSelection = false;

    supportedCountries: { [code: string]: GlobalizeCountryInfo } = {};

    error: string | null = null;

    async created(): Promise<void> {
        this.form.client_id = new URLSearchParams(window.location.search).get('client_id');
        if (!this.form.client_id) {
            window.location.href = urlJoin(this.$whitelabel.login.fallback_app_url, computeAutoselectDashboardQueryString());
            return;
        }

        this.supportedCountries = this.$localization.supportedCountries;
        this.form.company_country = this.$localization.locale.territory;

        this.localizationListener = this.$localization.on(
            'locale-change',
            () => {
                this.supportedCountries = this.$localization.supportedCountries;

                this.options = {
                    organiser: this.$t('login.signup.question.organiser') as string,
                    ticket_buyer: this.$t('login.signup.question.ticket_buyer') as string,
                };

                if (!this.isCompanyCountryDirty) {
                    this.form.company_country = this.$localization.locale.territory;
                }
            },
        );

        const http = Axios.create();

        // TODO - @openticket/lib-auth
        const r: {data: SignupRules} = await http.get(urlJoin(this.$whitelabel.login.auth_api_url, 'signup', 'rules'));
        this.rules = (r).data;

        // Manually add required phone rules as we make it mandatory: DD-272E
        this.rules.user_phone = [ ...this.rules.user_phone, 'required', 'min:5' ];

        this.rules.accept_tos.forEach((rule) => {
            const split = rule.split(':');
            if (split.length === 2) {
                const [ rule_name, rule_value ]: [ string, string ] = split;
                this.validationRules.accept_tos[rule_name] = rule_value;
            }
        });
        this.rules.company_country.forEach((rule) => {
            const split = rule.split(':');
            if (split.length === 2) {
                const [ rule_name, rule_value ]: [ string, string ] = split;
                this.validationRules.company_country[rule_name] = rule_value;
            }
        });
        this.rules.company_currency.forEach((rule) => {
            const split = rule.split(':');
            if (split.length === 2) {
                const [ rule_name, rule_value ]: [ string, string ] = split;
                this.validationRules.company_currency[rule_name] = rule_value;
            }
        });
        this.rules.company_locale.forEach((rule) => {
            const split = rule.split(':');
            if (split.length === 2) {
                const [ rule_name, rule_value ]: [ string, string ] = split;
                this.validationRules.company_locale[rule_name] = rule_value;
            }
        });
        this.rules.company_name.forEach((rule) => {
            const split = rule.split(':');
            if (split.length === 2) {
                const [ rule_name, rule_value ]: [ string, string ] = split;
                this.validationRules.company_name[rule_name] = rule_value;
            }
        });
        this.rules.company_timezone.forEach((rule) => {
            const split = rule.split(':');
            if (split.length === 2) {
                const [ rule_name, rule_value ]: [ string, string ] = split;
                this.validationRules.company_timezone[rule_name] = rule_value;
            }
        });
        this.rules.company_website.forEach((rule) => {
            const split = rule.split(':');
            if (split.length === 2) {
                const [ rule_name, rule_value ]: [ string, string ] = split;
                this.validationRules.company_website[rule_name] = rule_value;
            }
        });
        this.rules.user_email.forEach((rule) => {
            const split = rule.split(':');
            if (split.length === 2) {
                const [ rule_name, rule_value ]: [ string, string ] = split;
                this.validationRules.user_email[rule_name] = rule_value;
            }
        });
        this.rules.user_name.forEach((rule) => {
            const split = rule.split(':');
            if (split.length === 2) {
                const [ rule_name, rule_value ]: [ string, string ] = split;
                this.validationRules.user_name[rule_name] = rule_value;
            }
        });
        this.rules.user_password.forEach((rule) => {
            const split = rule.split(':');
            if (split.length === 2) {
                const [ rule_name, rule_value ]: [ string, string ] = split;
                this.validationRules.user_password[rule_name] = rule_value;
            }
        });
        this.rules.user_phone.forEach((rule) => {
            const split = rule.split(':');
            if (split.length === 2) {
                const [ rule_name, rule_value ]: [ string, string ] = split;
                this.validationRules.user_phone[rule_name] = rule_value;
            }
        });
    }

    beforeDestroy() {
        this.$localization.off(this.localizationListener);
    }

    @Watch('form.company_country', { immediate: true })
    onCountryChanged(): void {
        if (!this.form.company_country) {
            return;
        }

        const supportedCountry: GlobalizeCountryInfo | undefined = this.supportedCountries[this.form.company_country];

        // Set timezone data if field is clean
        if (!this.isCompanyTimezoneDirty && supportedCountry.defaultTimezones && supportedCountry.defaultTimezones.length === 1) {
            [ this.form.company_timezone ] = this.supportedCountries[this.form.company_country].defaultTimezones;
        }

        // Set country code if field is clean and empty in next tick
        if (!this.isUserPhoneDirty && supportedCountry.internationalCallingCode) {
            this.form.user_phone = `${this.supportedCountries[this.form.company_country].internationalCallingCode}00`;
            this.$nextTick(() => {
                this.form.user_phone = null;
            });
        }
    }

    onTimezoneSelection() {
        this.validationErrors.company_timezone = [];
        this.timeZoneSelection = false;
        this.isCompanyTimezoneDirty = true;
    }

    onCompanyCountrySelection() {
        this.validationErrors.company_country = [];
        this.isCompanyCountryDirty = true;
    }

    get url(): string | undefined {
        return this.$whitelabel.order.support_url;
    }

    get email(): string {
        return this.$whitelabel.order.support_email;
    }

    get ticketSupportEmailTitle(): string {
        return this.$t('login.signup.ticket_buyer.ticket_support_email.title') as string;
    }

    goToSupportUrl(): void {
        if (this.url) {
            window.location.href = this.url;
        }
    }

    get countries(): Record<string, string> {
        const map: Record<string, string> = {};

        const sortedCountries = Object.entries(this.supportedCountries).sort(
            (
                a: [string, GlobalizeCountryInfo],
                b: [string, GlobalizeCountryInfo],
            ) => a[1].territoryDisplayName.localeCompare(b[1].territoryDisplayName),
        );

        // Sort countries by the display name
        for (const [ countryKey, country ] of sortedCountries) {
            map[countryKey] = `${country.flag} ${country.territory} - ${country.territoryDisplayName}`;
        }

        return map;
    }

    getValidationError(errors: string[] | null | undefined, attributes: { [key: string]: string }): string {
        if (errors && errors.length > 0) {
            return this.$t(errors[0], attributes) as string;
        }
        return '';
    }

    get formCompleted(): boolean {
        return !!this.form
            && !!this.form.company_name
            && !!this.form.company_country
            && !!this.form.company_timezone
            && !!this.form.user_name
            && !!this.form.user_email
            && !!this.form.user_password
            && !!this.form.user_password_confirmation
            && !!this.form.user_phone
            && this.form.accept_tos;
    }

    async submit(): Promise<void> {
        this.deduceLocationParameters();

        this.error = null;

        const http = Axios.create();

        // TODO - @openticket/lib-auth
        try {
            // Manually validate user phone: DD-272E
            this.validatePhone();
            await http.post<{[key: string]:string} & { user: {[key: string]: string} }>(urlJoin(this.$whitelabel.login.auth_api_url, 'signup'), this.form).then((response) => {
                try {
                    const user = {
                        guid: response.data.user?.guid,
                        name: response.data.user?.name,
                        email: response.data.user?.email,
                        phone: response.data.user?.phone,
                        default_company_id: response.data.user?.default_company_id,
                        whitelabel_id: response.data.user?.whitelabel_id,
                    };

                    const trackingData = {
                        company_id: response.data.guid,
                        name: response.data.name,
                        country: response.data.country,
                        locale: response.data.locale,
                        currency: response.data.currency,
                        timezone: response.data.timezone,
                    };

                    rudderAnalyticsIdentify(user.guid, user);
                    // Keep old 'table' format for now
                    rudderAnalyticsTrack('dashboard new signup', trackingData);

                    // Also sent in new format (login scoped)
                    rudderAnalyticsTrack('login new signup', trackingData);
                } catch (e) {
                    console.error('Error sending signup tracking data to rudderstack', e);
                }
            });

            this.$notifications.success(this.$t('login.signup.success'));

            this.$emit('submitted', this.form.user_email);
        } catch (e) {
            if (this.isSignupValidationError(e) && e.response) {
                this.validationErrors = e.response.data.error_description;
            } else if (e instanceof Error && (e.message.includes('required') || e.message.includes('digits'))) {
                // Phone error handling: DD-272E
                let errorMessage = this.$t(`validation.${e.message}`, this.validationRules.user_phone.attribute);

                if (e.message.includes('min_digits')) {
                    errorMessage = this.$t(`validation.${e.message}`, { attribute: this.validationRules.user_phone.attribute, min: this.validationRules.user_phone.min });
                }
                if (e.message.includes('max_digits')) {
                    errorMessage = this.$t(`validation.${e.message}`, { attribute: this.validationRules.user_phone.attribute, max: this.validationRules.user_phone.max });
                }
                this.validationErrors = { user_phone: [ errorMessage as string ] };
            } else if (this.isAxiosErrorWithDescription(e) && e.response) {
                this.error = e.response.data.error_description;
            } else {
                throw e;
            }
        }
    }

    isSignupValidationError(error: unknown): error is AxiosError<{ error_description: SignupValidationError }> {
        if (Axios.isAxiosError(error)) {
            if (error.response && (error.status === 406 || error.response.status === 406) && 'error_description' in error.response.data) {
                return true;
            }
        }
        return false;
    }

    isAxiosErrorWithDescription(error: unknown): error is AxiosError<{ error_description: string }> {
        if (Axios.isAxiosError(error)) {
            if (error.response && 'error_description' in error.response.data) {
                return true;
            }
        }

        return false;
    }

    deduceLocationParameters(): void {
        if (this.form.company_country) {
            this.form.company_currency = this.$localization.supportedCountries[this.form.company_country].defaultCurrency;
            this.form.company_locale = this.$localization.locale.locale;
        }
    }

    // Manual phone validation: DD-272E
    validatePhone(): void {
        if (!this.form.user_phone) {
            throw new Error('required');
        }

        if (this.form.user_phone.length <= Number(this.validationRules.user_phone.min)) {
            throw new Error('min_digits');
        }

        if (this.form.user_phone.length >= Number(this.validationRules.user_phone.max)) {
            throw new Error('max_digits');
        }
    }

    // Removed for phone not being nullable: DD-272E
    // deduceOptionals(): void {
    //     if (!this.isUserPhoneDirty) {
    //         this.form.user_phone = null;
    //     }
    // }

    @Watch('chosenOption')
    chosenOptionChanged(value): void {
        rudderAnalyticsTrack(`signup change ${value}`);
    }

}
</script>

<style lang="scss" scoped>
.signup {
    &__content {
        &__ticket_buyer {
            &__email_support {
                text-decoration: none;
            }

            &__terms {
                text-decoration: underline;
                color: var(--ot-color-core-brand-invert);
            }

            .ot-notification {
                margin: var(--ot-spacing-2xl) 0;
            }
        }

        &__organiser {
            .ot-button {
                margin-top: var(--ot-spacing-3xl);
            }

          .country-timezone {
            display: flex;
            align-items: center;
            gap: var(--ot-spacing-2xs);
            margin-bottom: -1rem;

            &__timezone {
              flex-grow: 1;
            }
          }
        }

        &__back {
            color: var(--ot-color-core-brand);
            margin-top: var(--ot-spacing-2xl);
        }
    }
}

.terms-checkbox:focus-visible + .terms-label:before,
.terms-checkbox:focus-visible + .terms-label:after {
    outline: 2px solid var(--ot-input-border-color-focus);
    outline-offset: 2px;
}

.signup-button:focus,
.signup-button:focus-visible {
    outline: 2px solid var(--ot-input-border-color-focus);
    outline-offset: 2px;
}

.warning.remove-margin-bottom {
    margin-bottom: 0;
}

.warning {
    color: var(--ot-color-accent-orange-dark);
    text-align: center;
    margin-bottom: var(--ot-spacing-3xl);
}
</style>
